import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Droplets, Trophy, Users, School, ArrowRight, Star } from 'lucide-react';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';

const Home = ({ user, onLogout }) => {
  return (
    <div className="min-h-screen">
      <Navigation user={user} onLogout={onLogout} transparent />

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center hero-gradient pt-20">
        <div className="max-w-7xl mx-auto px-6 py-20 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="text-5xl lg:text-7xl font-extrabold text-slate-900 mb-6 tracking-tight leading-tight">
              Dive Into
              <span className="block text-cyan-700 mt-2">Excellence</span>
            </h1>
            <p className="text-xl text-slate-600 mb-8 leading-relaxed">
              Building confidence and champions in the water. From first splash to competitive excellence,
              we're Ghana's trusted partner in swimming education.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/book-lesson">
                <Button className="btn-primary" size="lg" data-testid="book-now-btn">
                  Book Your First Lesson
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <Link to="/swim-team">
                <Button className="btn-secondary" size="lg" data-testid="join-team-btn">
                  Join the Marlins
                </Button>
              </Link>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="relative"
          >
            <div className="rounded-3xl overflow-hidden shadow-2xl border-4 border-cyan-600">
              <img
                src="https://images.pexels.com/photos/12918910/pexels-photo-12918910.png"
                alt="Child learning to swim"
                className="w-full h-[500px] object-cover"
              />
            </div>
            <div className="absolute -bottom-6 -left-6 bg-orange-500 text-white rounded-2xl p-6 shadow-xl">
              <div className="text-4xl font-bold">10+</div>
              <div className="text-sm">Years of Excellence</div>
            </div>
          </motion.div>
        </div>

        {/* Wave Divider */}
        <div className="wave-divider">
          <svg viewBox="0 0 1200 120" preserveAspectRatio="none">
            <path
              d="M321.39,56.44c58-10.79,114.16-30.13,172-41.86,82.39-16.72,168.19-17.73,250.45-.39C823.78,31,906.67,72,985.66,92.83c70.05,18.48,146.53,26.09,214.34,3V0H0V27.35A600.21,600.21,0,0,0,321.39,56.44Z"
              fill="#ffffff"
            />
          </svg>
        </div>
      </section>

      {/* Services Overview */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl lg:text-5xl font-bold text-slate-900 mb-4">
              What We Offer
            </h2>
            <p className="text-xl text-slate-600 max-w-2xl mx-auto">
              Comprehensive swimming programs for all ages and skill levels
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                icon: Droplets,
                title: 'Children Lessons',
                description: 'Fun, safe environment for kids to learn swimming basics',
                color: 'bg-cyan-100 text-cyan-700',
                link: '/book-lesson',
              },
              {
                icon: Users,
                title: 'Adult Lessons',
                description: 'Never too late to learn. Private and group sessions available',
                color: 'bg-blue-100 text-blue-700',
                link: '/book-lesson',
              },
              {
                icon: Trophy,
                title: 'Marlins Team',
                description: 'Competitive training for aspiring champions',
                color: 'bg-orange-100 text-orange-700',
                link: '/swim-team',
              },
              {
                icon: School,
                title: 'School Programs',
                description: 'Partnership programs tailored for educational institutions',
                color: 'bg-purple-100 text-purple-700',
                link: '/partnerships',
              },
            ].map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <Link to={service.link}>
                  <div
                    className="bg-white rounded-2xl p-8 border border-slate-100 shadow-lg card-hover h-full"
                    data-testid={`service-card-${index}`}
                  >
                    <div className={`w-14 h-14 ${service.color} rounded-xl flex items-center justify-center mb-6`}>
                      <service.icon className="w-7 h-7" />
                    </div>
                    <h3 className="text-xl font-bold text-slate-900 mb-3">{service.title}</h3>
                    <p className="text-slate-600 leading-relaxed">{service.description}</p>
                    <div className="mt-4 flex items-center text-cyan-700 font-medium">
                      Learn more <ArrowRight className="ml-2 w-4 h-4" />
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-4xl lg:text-5xl font-bold text-slate-900 mb-6">
                Why Families Trust Us
              </h2>
              <div className="space-y-6">
                {[
                  { title: 'Certified Instructors', desc: 'All our coaches are internationally certified and experienced' },
                  { title: 'Safe Environment', desc: 'Safety-first approach with modern facilities and equipment' },
                  { title: 'Flexible Scheduling', desc: 'Choose times that work for your family' },
                  { title: 'Proven Results', desc: 'Track record of transforming beginners into confident swimmers' },
                ].map((item, i) => (
                  <div key={i} className="flex space-x-4">
                    <div className="flex-shrink-0">
                      <div className="w-10 h-10 bg-cyan-700 rounded-full flex items-center justify-center">
                        <Star className="w-5 h-5 text-white fill-current" />
                      </div>
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-slate-900 mb-1">{item.title}</h3>
                      <p className="text-slate-600">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="rounded-3xl overflow-hidden shadow-2xl border-4 border-cyan-600"
            >
              <img
                src="https://images.pexels.com/photos/9030294/pexels-photo-9030294.jpeg"
                alt="Swimming pool"
                className="w-full h-[500px] object-cover"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-cyan-700 to-cyan-600 text-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl lg:text-5xl font-bold mb-6">
              Ready to Make a Splash?
            </h2>
            <p className="text-xl mb-8 text-cyan-50">
              Join hundreds of satisfied families who've discovered the joy of swimming with us
            </p>
            <Link to="/book-lesson">
              <Button
                size="lg"
                className="bg-orange-500 hover:bg-orange-600 text-white rounded-full px-10 py-6 text-lg font-semibold shadow-2xl"
                data-testid="cta-book-btn"
              >
                Book Your First Lesson Today
                <ArrowRight className="ml-2 w-6 h-6" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Home;
